import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AttachedContextItem } from './interfaces/chat-component.interfaces';

@Injectable({
  providedIn: 'root'
})
export class AttachedContextService {
  private attachedItemsSubject = new BehaviorSubject<AttachedContextItem[]>([]);
  public attachedItems$: Observable<AttachedContextItem[]> = this.attachedItemsSubject.asObservable();
  private readonly entityBackedTypes = new Set<AttachedContextItem['type']>([
    'business',
    'partner',
    'funnel',
    'page',
    'form',
    'plan',
    'note',
    'proposal',
    'task',
    'ticket',
    'app',
    'media'
  ]);

  constructor() { }

  /**
   * Get current attached items
   */
  getAttachedItems(): AttachedContextItem[] {
    return this.attachedItemsSubject.value;
  }

  /**
   * Add a new context item
   */
  addItem(item: AttachedContextItem): void {
    const normalizedItem = this.normalizeItem(item);
    const currentItems = this.attachedItemsSubject.value;
    
    // Check if item already exists (by id or by filePath for files)
    const exists = currentItems.some(existing => 
      existing.id === normalizedItem.id ||
      (normalizedItem.filePath && existing.filePath === normalizedItem.filePath) ||
      (normalizedItem.entityId && existing.type === normalizedItem.type && existing.entityId === normalizedItem.entityId)
    );
    
    if (!exists) {
      this.attachedItemsSubject.next([...currentItems, normalizedItem]);
    }
  }

  /**
   * Remove a context item
   */
  removeItem(item: AttachedContextItem): void {
    const currentItems = this.attachedItemsSubject.value;
    const updatedItems = currentItems.filter(i => i.id !== item.id);
    this.attachedItemsSubject.next(updatedItems);
  }

  /**
   * Clear all attached items
   */
  clearAll(): void {
    this.attachedItemsSubject.next([]);
  }

  /**
   * Add a file from the editor
   */
  addFileFromEditor(filePath: string, fileName: string, content?: string): void {
    const item: AttachedContextItem = {
      id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'file',
      displayName: fileName,
      filePath: filePath,
      content: content,
      icon: 'fa-file-code'
    };
    
    this.addItem(item);
  }

  /**
   * Add a text selection
   */
  addSelection(content: string, sourceName?: string): void {
    const item: AttachedContextItem = {
      id: `selection-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'selection',
      displayName: sourceName || 'Text Selection',
      content: content,
      icon: 'fa-text-slash'
    };
    
    this.addItem(item);
  }

  /**
   * Add a plan reference
   */
  addBusiness(businessId: string, businessName: string): void {
    const item: AttachedContextItem = {
      id: `business-${businessId}`,
      type: 'business',
      displayName: businessName,
      entityId: businessId,
      icon: 'fa-building'
    };
    
    this.addItem(item);
  }

  addPartner(partnerId: string, partnerName: string): void {
    const item: AttachedContextItem = {
      id: `partner-${partnerId}`,
      type: 'partner',
      displayName: partnerName,
      entityId: partnerId,
      icon: 'fa-handshake'
    };

    this.addItem(item);
  }

  addFunnel(funnelId: string, funnelName: string): void {
    const item: AttachedContextItem = {
      id: `funnel-${funnelId}`,
      type: 'funnel',
      displayName: funnelName,
      entityId: funnelId,
      icon: 'fa-filter'
    };

    this.addItem(item);
  }

  addPage(pageId: string, pageName: string, parentId?: string, parentType?: AttachedContextItem['parentType'], parentDisplayName?: string): void {
    const item: AttachedContextItem = {
      id: `page-${pageId}`,
      type: 'page',
      displayName: pageName,
      entityId: pageId,
      icon: 'fa-file-lines',
      parentId,
      parentType,
      parentDisplayName
    };

    this.addItem(item);
  }

  addForm(formId: string, formName: string, parentId?: string, parentType?: AttachedContextItem['parentType'], parentDisplayName?: string): void {
    const item: AttachedContextItem = {
      id: `form-${formId}`,
      type: 'form',
      displayName: formName,
      entityId: formId,
      icon: 'fa-rectangle-list',
      parentId,
      parentType,
      parentDisplayName
    };

    this.addItem(item);
  }

  addPlan(planId: string, planName: string, parentId?: string, parentType?: AttachedContextItem['parentType'], parentDisplayName?: string): void {
    const item: AttachedContextItem = {
      id: `plan-${planId}`,
      type: 'plan',
      displayName: planName,
      entityId: planId,
      icon: 'fa-list-check',
      parentId,
      parentType,
      parentDisplayName
    };
    
    this.addItem(item);
  }

  /**
   * Add a note reference
   */
  addNote(noteId: string, noteName: string, parentId?: string, parentType?: AttachedContextItem['parentType'], parentDisplayName?: string): void {
    const item: AttachedContextItem = {
      id: `note-${noteId}`,
      type: 'note',
      displayName: noteName,
      entityId: noteId,
      icon: 'fa-note-sticky',
      parentId,
      parentType,
      parentDisplayName
    };
    
    this.addItem(item);
  }

  /**
   * Add a proposal reference
   */
  addProposal(proposalId: string, proposalName: string, parentId?: string, parentType?: AttachedContextItem['parentType'], parentDisplayName?: string): void {
    const item: AttachedContextItem = {
      id: `proposal-${proposalId}`,
      type: 'proposal',
      displayName: proposalName,
      entityId: proposalId,
      icon: 'fa-file-signature',
      parentId,
      parentType,
      parentDisplayName
    };
    
    this.addItem(item);
  }

  /**
   * Add a task reference
   */
  addTask(taskId: string, taskName: string, parentId?: string, parentType?: AttachedContextItem['parentType'], parentDisplayName?: string): void {
    const item: AttachedContextItem = {
      id: `task-${taskId}`,
      type: 'task',
      displayName: taskName,
      entityId: taskId,
      icon: 'fa-square-check',
      parentId,
      parentType,
      parentDisplayName
    };
    
    this.addItem(item);
  }

  /**
   * Add a ticket reference
   */
  addTicket(ticketId: string, ticketName: string, parentId?: string, parentType?: AttachedContextItem['parentType'], parentDisplayName?: string): void {
    const item: AttachedContextItem = {
      id: `ticket-${ticketId}`,
      type: 'ticket',
      displayName: ticketName,
      entityId: ticketId,
      icon: 'fa-headset',
      parentId,
      parentType,
      parentDisplayName
    };
    
    this.addItem(item);
  }

  /**
   * Add an app reference
   */
  addApp(appId: string, appName: string, parentId?: string, parentType?: AttachedContextItem['parentType'], parentDisplayName?: string): void {
    const item: AttachedContextItem = {
      id: `app-${appId}`,
      type: 'app',
      displayName: appName,
      entityId: appId,
      icon: 'fa-rocket',
      parentId,
      parentType,
      parentDisplayName
    };
    
    this.addItem(item);
  }

  /**
   * Add a media/file reference
   */
  addMedia(mediaId: string, mediaName: string, url?: string, mediaType?: string, parentId?: string, parentType?: AttachedContextItem['parentType'], parentDisplayName?: string): void {
    const item: AttachedContextItem = {
      id: `media-${mediaId}`,
      type: 'media',
      displayName: mediaName,
      entityId: mediaId,
      url: url,
      mediaType: mediaType,
      icon: this.getMediaIcon(mediaType),
      parentId,
      parentType,
      parentDisplayName
    };
    
    this.addItem(item);
  }

  /**
   * Get appropriate icon for media type
   */
  private getMediaIcon(mediaType?: string): string {
    if (!mediaType) return 'fa-file';
    
    const type = mediaType.toLowerCase();
    if (type.includes('image')) return 'fa-file-image';
    if (type.includes('video')) return 'fa-file-video';
    if (type.includes('audio')) return 'fa-file-audio';
    if (type.includes('pdf')) return 'fa-file-pdf';
    if (type.includes('word') || type.includes('document')) return 'fa-file-word';
    if (type.includes('excel') || type.includes('spreadsheet')) return 'fa-file-excel';
    if (type.includes('zip') || type.includes('archive')) return 'fa-file-zipper';
    return 'fa-file';
  }

  private normalizeItem(item: AttachedContextItem): AttachedContextItem {
    const entityId = item.entityId || this.inferEntityId(item);

    return {
      ...item,
      ...(entityId ? { entityId } : {})
    };
  }

  private inferEntityId(item: AttachedContextItem): string | undefined {
    if (!this.entityBackedTypes.has(item.type)) {
      return undefined;
    }

    const prefix = `${item.type}-`;
    if (typeof item.id === 'string' && item.id.startsWith(prefix) && item.id.length > prefix.length) {
      return item.id.slice(prefix.length);
    }

    return undefined;
  }

  // ============================================================
  // DRAG AND DROP UTILITIES
  // ============================================================

  /**
   * Create drag data for media items
   */
  createMediaDragData(media: any): any {
    return {
      type: 'media',
      id: media.id,
      displayName: media.displayName || media.name,
      url: media.url,
      mediaType: media.type
    };
  }

  /**
   * Create drag data for plans
   */
  createPlanDragData(plan: any): any {
    return {
      type: 'plan',
      id: plan.id,
      displayName: plan.name || plan.displayName
    };
  }

  /**
   * Create drag data for notes
   */
  createNoteDragData(note: any): any {
    return {
      type: 'note',
      id: note.id,
      displayName: note.name || note.title || note.displayName
    };
  }

  /**
   * Create drag data for proposals
   */
  createProposalDragData(proposal: any): any {
    return {
      type: 'proposal',
      id: proposal.id,
      displayName: proposal.name || proposal.title || proposal.displayName
    };
  }

  /**
   * Create drag data for tasks
   */
  createTaskDragData(task: any): any {
    return {
      type: 'task',
      id: task.id,
      displayName: task.name || task.title || task.displayName
    };
  }

  /**
   * Create drag data for tickets
   */
  createTicketDragData(ticket: any): any {
    return {
      type: 'ticket',
      id: ticket.id,
      displayName: ticket.name || ticket.title || ticket.subject || ticket.displayName
    };
  }

  /**
   * Create drag data for apps
   */
  createAppDragData(app: any): any {
    return {
      type: 'app',
      id: app.id,
      displayName: app.name || app.title || app.displayName
    };
  }

  /**
   * Create drag data for projects
   */
  createProjectDragData(project: any): any {
    return {
      type: 'project',
      id: project.id,
      displayName: project.name || 'Untitled Project'
    };
  }

  /**
   * Handle drag start event - reusable for any component
   * @param event DragEvent
   * @param item The item being dragged
   * @param dragDataCreator Function to create drag data specific to the item type
   */
  handleDragStart(event: DragEvent, item: any, dragDataCreator: (item: any) => any): void {
    const dragData = dragDataCreator(item);
    
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'copy';
      event.dataTransfer.setData('application/json', JSON.stringify(dragData));
      event.dataTransfer.setData('text/plain', dragData.displayName);
      
      // Create a custom drag image (compact chip)
      const dragImage = this.createDragPreviewChip(dragData.displayName, dragData.type);
      document.body.appendChild(dragImage);
      
      // Set the custom drag image with offset to center it on cursor
      event.dataTransfer.setDragImage(dragImage, dragImage.offsetWidth / 2, dragImage.offsetHeight / 2);
      
      // Remove the drag image after a short delay
      setTimeout(() => {
        document.body.removeChild(dragImage);
      }, 0);
      
            
      // Add visual feedback to the drag target
      const target = event.target as HTMLElement;
      if (target) {
        target.style.opacity = '0.5';
      }
    }
  }

  /**
   * Create a compact drag preview chip element
   * @param displayName The name to display on the chip
   * @param type The type of entity being dragged
   * @returns HTMLElement to use as drag image
   */
  private createDragPreviewChip(displayName: string, type: string): HTMLElement {
    const chip = document.createElement('div');
    
    // Get icon based on type
    const iconMap: Record<string, string> = {
      'business': 'fa-building',
      'partner': 'fa-handshake',
      'funnel': 'fa-filter',
      'page': 'fa-file-lines',
      'form': 'fa-rectangle-list',
      'media': 'fa-file',
      'plan': 'fa-list-check',
      'note': 'fa-note-sticky',
      'proposal': 'fa-file-signature',
      'task': 'fa-square-check',
      'ticket': 'fa-headset',
      'app': 'fa-rocket'
    };
    
    const icon = iconMap[type] || 'fa-file';
    
    // Style the chip - balanced prominence
    chip.style.cssText = `
      position: absolute;
      top: -1000px;
      left: -1000px;
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      background: rgba(40, 40, 50, 0.95);
      backdrop-filter: blur(10px);
      border: 1.5px solid rgba(140, 52, 234, 0.5);
      border-radius: 20px;
      color: rgba(255, 255, 255, 0.95);
      font-size: 14px;
      font-weight: 500;
      box-shadow: 0 4px 16px rgba(140, 52, 234, 0.25), 0 2px 8px rgba(0, 0, 0, 0.4);
      white-space: nowrap;
      max-width: 250px;
      pointer-events: none;
      z-index: 10000;
    `;
    
    // Truncate long names
    const truncatedName = displayName.length > 30 
      ? displayName.substring(0, 30) + '...' 
      : displayName;
    
    // Add icon and text
    chip.innerHTML = `
      <i class="fa-solid ${icon}" style="font-size: 14px;"></i>
      <span style="overflow: hidden; text-overflow: ellipsis;">${truncatedName}</span>
    `;
    
    return chip;
  }

  /**
   * Handle drag end event - restore visual state
```
  }

  /**
   * Handle drag end event - restore visual state
   */
  handleDragEnd(event: DragEvent): void {
    const target = event.target as HTMLElement;
    if (target) {
      target.style.opacity = '1';
    }
  }
}
